/**
 * agnos.ai Icon System - Single Source of Truth
 *
 * This file is the CORE of the entire icon/favicon system. It provides utilities
 * that work correctly in both React components and Next.js ImageResponse contexts.
 *
 * CRITICAL: ImageResponse Compatibility
 *
 * Next.js ImageResponse uses Chromium's layout engine to rasterize JSX/SVG into PNG.
 * This creates specific sensitivities that this file addresses:
 *
 * 1. DIMENSION REQUIREMENTS:
 *    - ImageResponse: MUST use explicit pixel dimensions (width: 32, height: 32)
 *    - React: Can use percentage dimensions for responsive behavior
 *
 * 2. PRESERVEASPECTRATIO SELECTION:
 *    - ImageResponse: Uses "xMidYMid meet" (prevents clipping, ensures full visibility)
 *    - React: Uses "xMidYMid slice" (allows clipping for responsive design)
 *
 * 3. LAYOUT ENGINE SENSITIVITIES:
 *    - NO wrapper divs around SVG elements when used with ImageResponse
 *    - NO percentage dimensions (width="100%") in ImageResponse context
 *    - ViewBox coordinates must remain consistent (0 0 200 200)
 *
 * CRITICAL: No Hardcoded Colors
 * ===============================
 *
 * This system NEVER uses hardcoded hex values. All colors must be provided
 * explicitly via props or CSS variables. This ensures:
 *
 * 1. globals.css remains the single source of truth
 * 2. No color inconsistencies between contexts
 * 3. Proper theme support and color management
 * 4. Fail-fast behavior when colors are missing
 *
 * USAGE PATTERNS:
 *
 * // React component (with CSS variables)
 * generateIconElements({ variant: "gradient", gradientStart: "#7b53ff", gradientEnd: "#5e20e4" })
 *
 * // ImageResponse (explicit hex colors required)
 * generateIconElements({ variant: "gradient", gradientStart: "#7b53ff", gradientEnd: "#5e20e4", width: 32, height: 32 })
 *
 * The system automatically detects the context and applies appropriate settings.
 *
 * @see README.md for comprehensive documentation
 * @see /app/(utils)/icon/route.tsx for ImageResponse implementation example
 */

export enum IconVariant {
  GRADIENT = "gradient",
  SOLID = "solid",
  CURRENT_COLOR = "currentColor",
  BRAND_GRADIENT = "brand-gradient",
}

export type IconOptions = {
  gradientStart?: string
  gradientEnd?: string
  variant?: IconVariant
  // CRITICAL: Width and height parameters for ImageResponse compatibility
  // - ALWAYS provide both width and height when using with ImageResponse
  // - Omitting these causes layout engine to use percentages, leading to rendering issues
  // - For React components, these can be omitted (will use 100% dimensions)
  width?: number
  height?: number
  bimi?: boolean
}

// Define types for our SVG data
type GradientStop = {
  offset: string
  color: string
}

type GradientDefinition = {
  id: string
  x1: string
  y1: string
  x2: string
  y2: string
  stops: GradientStop[]
}

type DotPath = {
  opacity: string
  data: string
}

// Base attributes all variants have
type BaseSVGAttributes = {
  version?: string
  xmlns: string
  viewBox: string
  preserveAspectRatio: string
  circleFill: string
  mainPathData: string
  dotPaths: DotPath[]
  baseProfile?: string
}

// Gradient variant has additional gradient property
type GradientSVGAttributes = BaseSVGAttributes & {
  gradient: GradientDefinition
}

// Union type to handle both cases
type SVGAttributes = GradientSVGAttributes | BaseSVGAttributes

// This function returns plain serializable objects (not JSX) that can be used with
// both regular components and route handlers via ImageResponse
export function generateSVGAttributes(
  options: IconOptions = {}
): SVGAttributes {
  const {
    variant = IconVariant.BRAND_GRADIENT,
    gradientStart,
    gradientEnd,
    bimi = false,
  } = options

  // Common SVG attributes
  // CRITICAL: preserveAspectRatio must be "xMidYMid slice" to prevent icon cutting off
  // "slice" ensures the SVG covers the entire viewbox even if some content is clipped
  // "meet" would make the SVG fit inside the viewbox which can cause cutting off
  const svgAttrs = {
    version: "1.2",
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 200 200",
    preserveAspectRatio: bimi ? "xMidYMid meet" : "xMidYMid slice",
    baseProfile: "tiny-ps",
  }

  // The path data for the main "a" letter shape
  const mainPathData =
    "M116.11,126.65c-4.25,6.38-14.17,8.79-19.27,8.79C80,135.44,63.1,122.26,63.1,101S80,66.56,96.84,66.56c4.25,0,14.17,1.41,19.27,8.79V68h19.56v66.2H116.11Zm-16-9.92c8.08,0,16-5.81,16-15.73s-7.93-15.73-16-15.73S83.51,91.51,83.51,101,91.88,116.73,100.1,116.73Z"

  // The data for decorative dots
  const dotPaths = [
    {
      opacity: "0.5",
      data: "M100.33,17.82c6.78,0,12.55,5.05,12.55,12s-5.77,12.12-12.55,12.12A12.08,12.08,0,0,1,87.92,29.8C87.92,22.87,93.55,17.82,100.33,17.82Z",
    },
    {
      opacity: "0.3",
      data: "M58.15,28.6C64,25.21,71.54,26.7,75,32.7s1.06,13.38-4.82,16.77a12.07,12.07,0,0,1-16.8-4.29C49.93,39.18,52.28,32,58.15,28.6Z",
    },
    {
      opacity: "0.15",
      data: "M27.29,59.52c3.39-5.88,10.65-8.35,16.65-4.89s7.6,11.06,4.21,16.93a12.05,12.05,0,0,1-16.69,4.69C25.46,72.79,23.9,65.39,27.29,59.52Z",
    },
    {
      opacity: "1",
      data: "M184.79,102.14c0,6.78-5,12.55-12,12.55s-12.12-5.77-12.12-12.55a12.08,12.08,0,0,1,12.12-12.41C179.74,89.73,184.79,95.36,184.79,102.14Z",
    },
    {
      opacity: "0.85",
      data: "M173.45,60c3.39,5.88,1.9,13.4-4.1,16.86S156,77.88,152.58,72a12.07,12.07,0,0,1,4.29-16.8C162.87,51.73,170.06,54.08,173.45,60Z",
    },
    {
      opacity: "0.65",
      data: "M142.53,29.09c5.88,3.39,8.35,10.65,4.89,16.65S136.36,53.35,130.49,50a12.07,12.07,0,0,1-4.69-16.7C129.26,27.26,136.66,25.7,142.53,29.09Z",
    },
  ]

  // Different fill strategies based on variant
  if (variant === IconVariant.GRADIENT) {
    if (!gradientStart || !gradientEnd) {
      throw new Error(
        "gradientStart and gradientEnd are required for gradient variant"
      )
    }

    return {
      ...svgAttrs,
      gradient: {
        id: "agnos-icon-gradient",
        x1: "0%",
        y1: "0%",
        x2: "0%",
        y2: "100%",
        stops: [
          {
            offset: "0%",
            color: gradientStart,
          },
          { offset: "100%", color: gradientEnd },
        ],
      },
      circleFill: "url(#agnos-icon-gradient)",
      mainPathData,
      dotPaths,
    }
  }

  if (variant === IconVariant.SOLID) {
    return {
      ...svgAttrs,
      circleFill: "var(--color-primary)",
      mainPathData,
      dotPaths,
    }
  }

  if (variant === IconVariant.CURRENT_COLOR) {
    return {
      ...svgAttrs,
      circleFill: "currentColor",
      mainPathData,
      dotPaths,
    }
  }

  if (variant === IconVariant.BRAND_GRADIENT) {
    return {
      ...svgAttrs,
      gradient: {
        id: "agnos-brand-gradient",
        x1: "0%",
        y1: "0%",
        x2: "0%",
        y2: "100%",
        stops: [
          {
            offset: "0%",
            color: "var(--color-brand-gradient-start)",
          },
          {
            offset: "100%",
            color: "var(--color-brand-gradient-end)",
          },
        ],
      },
      circleFill: "url(#agnos-brand-gradient)",
      mainPathData,
      dotPaths,
    }
  }

  // Default fallback to gradient with required colors
  if (!gradientStart || !gradientEnd) {
    throw new Error(
      "gradientStart and gradientEnd are required for gradient variant"
    )
  }

  return {
    ...svgAttrs,
    gradient: {
      id: "agnos-icon-gradient",
      x1: "0%",
      y1: "0%",
      x2: "0%",
      y2: "100%",
      stops: [
        {
          offset: "0%",
          color: gradientStart,
        },
        { offset: "100%", color: gradientEnd },
      ],
    },
    circleFill: "url(#agnos-icon-gradient)",
    mainPathData,
    dotPaths,
  }
}

// CRITICAL: SVG rendering configuration documentation
//
// This function generates JSX elements that work in two different contexts:
// 1. React components (responsive, uses percentage dimensions)
// 2. ImageResponse (requires explicit pixel dimensions)
//
// The key challenge: ImageResponse uses Chromium's layout engine to rasterize JSX/SVG
// into PNG, which has specific requirements that differ from normal React rendering.
//
// IMAGERESPONSE SENSITIVITIES:
// - Must use explicit pixel dimensions (width="32" not width="100%")
// - Must use preserveAspectRatio="xMidYMid meet" to prevent clipping
// - Cannot wrap SVG in container divs (breaks layout calculation)
// - ViewBox coordinates must match the design (0 0 200 200)
// - Gradients must use objectBoundingBox for proper rendering in ImageResponse
//
// This function automatically handles these differences based on the presence
// of width/height parameters.
export function generateIconElements(options: IconOptions = {}) {
  const svgData = generateSVGAttributes(options)
  const { width, height } = options

  // Generate a unique ID for the gradient
  const uniqueId = `agnos-gradient-${Math.random().toString(36).substring(2, 11)}`

  // CRITICAL: Conditional rendering configuration for ImageResponse compatibility
  //
  // The layout engine in ImageResponse (Chromium) interprets SVG differently than browsers:
  //
  // 1. EXPLICIT DIMENSIONS (when width/height provided):
  //    - Uses "32" instead of "100%" to give layout engine concrete pixel targets
  //    - Uses preserveAspectRatio="xMidYMid meet" to ensure entire icon is visible
  //    - "meet" ensures no clipping (entire viewBox fits within the specified dimensions)
  //
  // 2. RESPONSIVE DIMENSIONS (when width/height omitted):
  //    - Uses "100%" for flexible sizing in React components
  //    - Uses preserveAspectRatio="xMidYMid slice" to allow clipping for responsive design
  //    - "slice" ensures the viewBox covers the entire container, cropping if necessary
  //
  // 3. GRADIENT COMPATIBILITY:
  //    - ImageResponse requires objectBoundingBox gradientUnits
  //    - Browser SVG can use either objectBoundingBox or userSpaceOnUse
  //    - We use objectBoundingBox for maximum compatibility
  //
  // DO NOT CHANGE: These settings prevent the "tiny white square" issue in ImageResponse
  const svgWidth = width ? `${width}` : "100%"
  const svgHeight = height ? `${height}` : "100%"
  const aspectRatio = width && height ? "xMidYMid meet" : "xMidYMid slice"
  const gradientUnits = "objectBoundingBox" // ImageResponse compatibility

  // CRITICAL: Direct SVG element rendering
  // - No wrapper divs allowed when used with ImageResponse
  // - Layout engine needs to process the SVG element directly
  // - ViewBox="0 0 200 200" coordinates system:
  //   * Circle center: (100, 100) with radius 100
  //   * All paths designed for 200x200 coordinate space
  //   * Changing viewBox requires updating all path data
  return (
    <svg
      version={svgData.version || "1.2"}
      xmlns={svgData.xmlns}
      viewBox={svgData.viewBox}
      width={svgWidth}
      height={svgHeight}
      preserveAspectRatio={aspectRatio}
      baseProfile={svgData.baseProfile || "full"}
    >
      {/* Gradient definition (only rendered for gradient variant) */}
      {"gradient" in svgData && (
        <defs>
          <linearGradient
            id={uniqueId}
            x1={svgData.gradient.x1}
            y1={svgData.gradient.y1}
            x2={svgData.gradient.x2}
            y2={svgData.gradient.y2}
            gradientUnits={gradientUnits}
          >
            {svgData.gradient.stops.map((stop, index) => (
              <stop
                key={index}
                offset={stop.offset}
                stopColor={stop.color}
              />
            ))}
          </linearGradient>
        </defs>
      )}

      {/* Main circular background - centered at (100,100) with radius 100 */}
      <circle
        fill={"gradient" in svgData ? `url(#${uniqueId})` : svgData.circleFill}
        cx="100"
        cy="100"
        r="100"
      />

      {/* Central "a" letter - extracted from authentic agnos.ai logo */}
      <path
        fill="white"
        d={svgData.mainPathData}
      />

      {/* Decorative dots with varying opacities - preserve brand authenticity */}
      {svgData.dotPaths.map((dot, index) => (
        <path
          key={index}
          fill="white"
          d={dot.data}
          fillOpacity={dot.opacity}
        />
      ))}
    </svg>
  )
}

/**
 * Generate SVG string content for standalone SVG files
 * This is different from generateIconElements which returns JSX
 */
export function generateSVGString(options: IconOptions = {}): string {
  // We can't use react-dom/server in Edge runtime, so this is a simplified renderer
  // It's not perfect but works for our specific SVG structure.

  const { width, height, variant, bimi = false } = options
  const attrs = generateSVGAttributes(options)

  const dimensions = bimi
    ? ""
    : width && height
      ? `width="${width}" height="${height}"`
      : `width="100%" height="100%"`

  const gradientDef =
    "gradient" in attrs
      ? `<defs><linearGradient id="${
          attrs.gradient.id
        }" x1="${attrs.gradient.x1}" y1="${attrs.gradient.y1}" x2="${
          attrs.gradient.x2
        }" y2="${attrs.gradient.y2}">${attrs.gradient.stops
          .map(
            stop =>
              `<stop offset="${stop.offset}" stop-color="${stop.color}" />`
          )
          .join("")}</linearGradient></defs>`
      : ""

  const circleOpacity =
    variant === IconVariant.CURRENT_COLOR ? 'opacity="0.1"' : ""

  const mainPathFill =
    variant === IconVariant.CURRENT_COLOR ? "currentColor" : "#fff"

  const dotsFill =
    variant === IconVariant.CURRENT_COLOR ? "currentColor" : "#fff"
  const dots = attrs.dotPaths
    .map(p =>
      bimi
        ? `<path d="${p.data}" fill-opacity="${p.opacity}" />`
        : `<path d="${p.data}" opacity="${p.opacity}" />`
    )
    .join("")

  return `<svg ${dimensions} version="${attrs.version}" xmlns="${
    attrs.xmlns
  }" viewBox="${attrs.viewBox}" preserveAspectRatio="${
    attrs.preserveAspectRatio
  }" baseProfile="${attrs.baseProfile}">
      <title>agnos.ai</title>
      ${gradientDef}
      <g ${circleOpacity}><circle cx="100" cy="100" r="100" fill="${
        attrs.circleFill
      }" /></g>
      <path d="${attrs.mainPathData}" fill="${mainPathFill}" />
      <g fill="${dotsFill}">${dots}</g>
    </svg>`
}
